import axios from 'axios'
import { env } from '@/config/env'
import { STORAGE_KEYS } from '@/constants'

/**
 * Axios instance for talking to the backend.
 *
 * Backend response envelope:
 *   success → { success, statusCode, message, data }
 *   error   → { success: false, statusCode, message, timestamp, path }
 *
 * The response interceptor unwraps `data` so callers see only the inner
 * payload. Auth tokens live in localStorage and are attached on every
 * request; a 401 triggers a single refresh attempt + retry before the
 * caller sees the failure.
 */

export const apiClient = axios.create({
  baseURL: env.apiBaseUrl,
  timeout: env.apiTimeoutMs,
  headers: { 'Content-Type': 'application/json' },
})

const tokenStore = {
  get access() {
    return localStorage.getItem(STORAGE_KEYS.accessToken)
  },
  get refresh() {
    return localStorage.getItem(STORAGE_KEYS.refreshToken)
  },
  setTokens(access, refresh) {
    if (access) localStorage.setItem(STORAGE_KEYS.accessToken, access)
    if (refresh) localStorage.setItem(STORAGE_KEYS.refreshToken, refresh)
  },
  clear() {
    localStorage.removeItem(STORAGE_KEYS.accessToken)
    localStorage.removeItem(STORAGE_KEYS.refreshToken)
    localStorage.removeItem(STORAGE_KEYS.authUser)
  },
}

export const authTokens = tokenStore

let onAuthFailure = null
export const setAuthFailureHandler = (fn) => {
  onAuthFailure = fn
}

apiClient.interceptors.request.use((config) => {
  const token = tokenStore.access
  if (token) {
    config.headers = config.headers ?? {}
    config.headers.Authorization = `Bearer ${token}`
  }
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    if (typeof config.headers?.delete === 'function') {
      config.headers.delete('Content-Type')
    } else if (config.headers) {
      delete config.headers['Content-Type']
      delete config.headers['content-type']
    }
  }
  return config
})

let refreshInFlight = null

async function performRefresh() {
  const refreshToken = tokenStore.refresh
  if (!refreshToken) throw new Error('No refresh token available')
  const response = await axios.post(
    `${env.apiBaseUrl}/auth/refresh`,
    { refreshToken },
    { timeout: env.apiTimeoutMs }
  )
  const body = response.data?.data ?? response.data
  const newAccess = body?.accessToken
  const newRefresh = body?.refreshToken
  if (!newAccess) throw new Error('Refresh response missing accessToken')
  tokenStore.setTokens(newAccess, newRefresh ?? refreshToken)
  return newAccess
}

apiClient.interceptors.response.use(
  (response) => {
    const body = response.data
    if (body && typeof body === 'object' && 'success' in body && 'data' in body) {
      return body.data
    }
    return body
  },
  async (error) => {
    const original = error?.config
    const status = error?.response?.status
    const isRefreshCall = original?.url?.includes('/auth/refresh')

    if (status === 401 && !original?._retried && !isRefreshCall && tokenStore.refresh) {
      original._retried = true
      try {
        refreshInFlight = refreshInFlight ?? performRefresh()
        const newAccess = await refreshInFlight
        refreshInFlight = null
        original.headers = original.headers ?? {}
        original.headers.Authorization = `Bearer ${newAccess}`
        return apiClient.request(original)
      } catch (refreshError) {
        refreshInFlight = null
        tokenStore.clear()
        if (onAuthFailure) onAuthFailure()
        if (env.isDev) {
          console.error('[apiClient] refresh failed', refreshError?.message)
        }
        return Promise.reject(refreshError)
      }
    }

    if (env.isDev) {
      console.error('[apiClient]', error?.message, error?.response?.data)
    }

    const wrapped = error?.response?.data
    if (wrapped && typeof wrapped === 'object' && wrapped.message) {
      error.userMessage = Array.isArray(wrapped.message)
        ? wrapped.message.join(', ')
        : wrapped.message
    }
    return Promise.reject(error)
  }
)

/** True when the app should hit a real backend instead of mock JSON. */
export const isRemoteApiEnabled = () => Boolean(env.apiBaseUrl)
